import CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';
import { sha256 } from 'js-sha256';
import {
  newTracker,
  trackPageView as spTrackPageView,
  trackSelfDescribingEvent,
  trackStructEvent,
  setUserIdFromCookie,
  clearUserData,
  enableActivityTracking,
  enableActivityTrackingCallback,
  crossDomainLinker,
} from '@snowplow/browser-tracker';
import { 
  enableDomClickTracking 
} from "./plugins/dom-click-tracking";
import {
  LinkClickTrackingPlugin,
  enableLinkClickTracking,
} from '@snowplow/browser-plugin-link-click-tracking';
import {
  FormTrackingPlugin,
  enableFormTracking,
} from '@snowplow/browser-plugin-form-tracking';
import { enableVirtualFormTracking } from './plugins/virtual-form-tracking';
import { enableGenericFormTracking,isNativeTrackable} from './plugins/generic-form-tracking';
import { enableEnterTracking } from './helpers/enter-tracking';
import { isFieldAllowed, FormFieldEncryption } from './form-field-privacy';
import { isEntryCta, sourceValue, label } from './helpers/click-source';
import {
  ButtonClickTrackingPlugin,
  enableButtonClickTracking,
  trackButtonClick,
} from '@snowplow/browser-plugin-button-click-tracking';
import {
  MediaTrackingPlugin,
  startHtml5MediaTracking
} from '@snowplow/browser-plugin-media-tracking';
import {
  startVimeoTracking,
  endVimeoTracking
} from '@snowplow/browser-plugin-vimeo-tracking';
import {
  SnowplowEcommercePlugin,
  enableEnhancedEcommerceTracking
} from './plugins/enhanced-ecommerce-tracking';

(function (window) {
  // 'use strict';

  let recommendParamsData = null;
  const processedCards = new WeakSet();
  let navHooksInstalled = false;

  function resetTrackingState() {
    aggregatedEvent = {
      minXOffset: Infinity,
      maxXOffset: 0,
      minYOffset: Infinity,
      maxYOffset: 0,
      numEvents: 0
    };

    pageStartTime = Date.now();
  }
  // Utility to dispatch an event only once
  function dispatchEventOnce(target, eventName) {
    if (!target || typeof target.dispatchEvent !== 'function') {
      throw new Error("Invalid target");
    }

    // Use a Symbol to avoid collisions in property names
    const flagKey = Symbol.for(`__eventDispatched_${eventName}`);

    // Check if already dispatched
    if (target[flagKey]) {
      console.log(`Event "${eventName}" already dispatched. Skipping.`);
      return false;
    }

    // Create and dispatch the event
    const event = new Event(eventName);
    target.dispatchEvent(event);

    // Mark as dispatched
    target[flagKey] = true;
    console.log(`Event "${eventName}" dispatched.`);
    return true;
  }

  function initTracker() {
    var trackerObj = {
      snowplowEnabled: false, //flag to denote if snowplow tracker was enabled for this instance
      collectorHost: "", //snowplow collector host
      appId: "", //snowplow app id
      spaObserver: null,
      initUrl: null,
      /*** **************************************************************************/
      /*** ************ Public methods - exposed for the container ************ *****/
      /*** **************************************************************************/

      initConfig(obj) {
        obj = obj || {};

        const snowplow = (obj.config && obj.config.snowplow) || obj;
        if (snowplow.appId && snowplow.cDomain) {
          this.snowplowEnabled = true;
          this.initSnowplowConfig(snowplow);
        } else {
          console.error("appId and cDomain are required.");
        }
        
        if (this.initUrl !== location.href && this.spaObserver && this.spaObserver.disconnect) {
          this.spaObserver.disconnect();
          this.spaObserver = true;
        }
        this.initUrl = location.href;
        if (this.snowplowEnabled && !this.spaObserver) {
          let lastUrl = location.href;
          this.spaObserver = new MutationObserver(() => {
            const url = location.href;
            if (url !== lastUrl) {
              console.log('pv evt occurred.');
              lastUrl = url;
              spTrackPageView();
            }
          });
          this.spaObserver.observe(document, { subtree: true, childList: true });
        }
        
        //Capture eloqua hash email from GET parameters
        this.setEloquaHashEmail();
        setTimeout(() => {
          dispatchEventOnce(document, "trackerLoaded");
      }, 2000);
      },

      setTrackingUID(userId) {
        if (this.snowplowEnabled) {
          if (userId) {
            /* document.cookie = "IRIS_ID=" + userId + "; path=/;"; */
            setDomainCookie(this.collectorHost, 'IRIS_ID', userId, true);
          }
          setUserIdFromCookie("IRIS_ID");
        }
      },

      clearTrackingUID() {
        try {
          if (this.snowplowEnabled) {
            let collectorName = this.collectorHost;
            let domainName = collectorName ? collectorName.substring(1, collectorName.length) : collectorName;
            if (collectorName && collectorName.startsWith('c.') && (window.location.hostname.indexOf(domainName) > 0)) {
              document.cookie = "IRIS_ID=; Max-Age=-99999999; path=/; domain=" + domainName + ";";
            }
            else {
              document.cookie = "IRIS_ID=; Max-Age=-99999999; path=/;";
            }
            clearUserData({ preserveSession: true, preserveUser: true });
          }
        } catch (rrr) {
          console.log('Could not clear the cookie');
        }
        //clear eloqua email
        /* document.cookie = "_iris_eloqua_em=; Max-Age=-99999999; path=/;";*/
      },

      trackImpression(data) {
        if (this.snowplowEnabled) {
          trackSelfDescribingEvent({
            event: {
              schema:
                "iglu:com.snowplowanalytics.snowplow/ad_impression/jsonschema/1-0-1",
              data: data,
            },
          });
        }
      },

      trackServedImpression(data) {
        if (this.snowplowEnabled) {
          trackSelfDescribingEvent({
            event: {
              schema: "iglu:com.iiris/served_impressions/jsonschema/1-0-2",
              data: {
                page_url: window.location.href ? window.location.href.substring(0,4096) : null,
                impression_id: data.impression_id,
                recommendation_id: data.recommendation_id || null
              }
            }
          });
        }
      },

      trackUriRedirect(data) {
        if (this.snowplowEnabled) {
          trackSelfDescribingEvent({
            event: {
              schema:
                "iglu:com.snowplowanalytics.snowplow/uri_redirect/jsonschema/1-0-0",
              data: {
                uri: data.url
              },
            },
          });
        }
      },

      trackStructEvt(data) {
        if (this.snowplowEnabled) {
          if (data) {
           trackSelfDescribingEvent({
           event: {
            schema: 'iglu:com.iiris/default_struct_event/jsonschema/1-0-0',
            data: {
             se_category: data.cat,
             se_action: data.act,
             se_label: data.lbl,
             se_property: data.prop,
             se_value:   data.val === undefined || data.val === null || data.val === ''
             ? null
             : Number.isNaN(Number(data.val))
             ? null
             : Number(data.val),
            },
          },
        });
      }
    }
  },

      trackPageView(data) {
        if (this.snowplowEnabled) {
          if (data && data.title) {
            spTrackPageView({ title: data.title });
          }
          else {
            spTrackPageView();
          }
        }
      },

      ad_impression: function (zone, redirectUrl, targetUrl, impression, recId, itemId, recipe) {
        if (!this.snowplowEnabled) return;

        if (zone === "click") {
          const ctrEvt = new CustomEvent("recommendCTREvent", {
            detail: { target_url: redirectUrl },
          });
          window.dispatchEvent(ctrEvt);
        }

        const bannerId = "iiris" + Date.now();
        const campaignId = "iiris_recommend";
        const advertiserId = "iiris";

        const data = {
          impressionId: impression,
          bannerId,
          campaignId,
          advertiserId,
          targetUrl: targetUrl,
          zoneId: zone,
          itemId: itemId,
          recommendationId: recId,
          recipe: recipe
        };

        if (zone === "viewport") {
          const viewportEvt = new CustomEvent("iiris_recommendViewportEvent", {
            detail: { impressionId: impression },
          });
          window.dispatchEvent(viewportEvt);
        }

        this.trackImpression(data);
      },

      trackUnloadEvent(cfg) {
        try {
            trackSelfDescribingEvent({
              event: {
                schema: "iglu:com.iiris/page_unload_event/jsonschema/1-0-4",
                data: this.getUnloadEvtData()
              },
              context: this.getContext(cfg.context)
            });
        } catch (error) {
          console.log("Error occurred during page_unload event");
        }
      },

      getUnloadEvtData() {
        const activeTime = Math.floor((Date.now() - pageStartTime) / 1000);
        return {
          minXOffset: isFinite(aggregatedEvent.minXOffset) ? Math.min(Math.max(Math.round(aggregatedEvent.minXOffset), 0), 600000) : 0,
          maxXOffset: isFinite(aggregatedEvent.maxXOffset) ? Math.min(Math.max(Math.round(aggregatedEvent.maxXOffset), 0), 600000) : 0,
          minYOffset: isFinite(aggregatedEvent.minYOffset) ? Math.min(Math.max(Math.round(aggregatedEvent.minYOffset), 0), 600000) : 0,
          maxYOffset: isFinite(aggregatedEvent.maxYOffset) ? Math.min(Math.max(Math.round(aggregatedEvent.maxYOffset), 0), 600000) : 0,
          activeSeconds: Math.min(Math.max(activeTime, 0),604800)
        };
      },

      handleCrossDomainLink(evt) {
        let rawUrl = evt.detail.url;
        let tgtUrl = new URL(evt.detail.url);
        let tgtHost = tgtUrl.hostname;
        let tgtPath = tgtUrl.pathname;
        let tgtSearch = tgtUrl.search;
        let duidCookie = getSnowplowDuid();
        if (!isSameDomainLink(rawUrl)) {
          if (duidCookie) {
            if (!tgtSearch.includes('_sp')) {
              if (tgtSearch.includes('?')) {
                tgtSearch = tgtSearch + '&_sp=' + duidCookie;
              }
              else {
                tgtSearch = tgtSearch + '?_sp=' + duidCookie;
              }
            }
          }
        }

        let tgt = '_blank';
        if (evt.detail.mode && evt.detail.mode == 'same') {
          tgt = '_self';
        }

        window.open('https://' + tgtHost + tgtPath + tgtSearch, tgt);
      },

      removeSpParam() {
        try {
          let cdUrl = new URL(window.location.href);
          if (cdUrl.searchParams.has('_sp')) {
            cdUrl.searchParams.delete('_sp');
            window.history.replaceState(window.history.state, '', cdUrl.pathname + cdUrl.search + cdUrl.hash);
          }
        } catch (e) { }
      },

      getUserId(userId) {
        try {
          if (userId && userId.trim() !== "") {
            return userId
          }
          else {
            return getCookie('IRIS_ID');
          }
        } catch (e) {
          console.log(e);
          return userId;
        }
      },

      getDUId() {
        let iris_cookies = document.cookie.split(';').reduce((cookies, cookie) => {
          const [name, value] = cookie.split('=').map(c => c.trim());
          cookies[name] = value;
          return cookies;
        }, {});

        let props = Object.getOwnPropertyNames(iris_cookies);

        let sp_cookie = '';

        for (var i = 0; i < props.length; i++) {
          if (props[i].startsWith('_sp_id.')) {
            sp_cookie = props[i];
            break;
          }
        }

        if (iris_cookies[sp_cookie]) {
          return iris_cookies[sp_cookie].split('.')[0];
        }
        else
          return null;
      },

      getUHash() {
        const params = new Proxy(
          new URLSearchParams(window.location.search),
          { get: (searchParams, prop) => searchParams.get(prop) }
        );

        let eUserHash = params.sp_eh ? params.sp_eh : getCookie('sp_eh');
        let userHash;
        let idtToken = getCookie('IRIS_IDT');
        if (idtToken) {
            try {
              const arrayToken = idtToken.split('.');
              if (arrayToken.length !== 3) {
                console.warn("Invalid IRIS_IDT token format");
              } else {
                const tokenPayload = JSON.parse(atob(arrayToken[1]));
                userHash = this.hashEmail(tokenPayload.email);
              }
            } catch (err) {
              console.error("Error parsing IRIS_IDT token:", err);
            }
        } else {
          console.warn("IRIS_IDT cookie does not exist");
        }

        // code changes for Scenario 2:
        if(userHash && userHash.trim() !== ""){
          return userHash;
        }
        // code changes for Scenario 1:
        if (eUserHash && eUserHash.trim() != "") {
          this.setUHash(eUserHash,'eUser')
          return eUserHash;
        }
        return null;
      },
        hashEmail(message) {
        if (!message) return null;
        const hashHex = sha256(message);
        return hashHex;
      },

      setUHash(hash,type) {
        setDomainCookie(this.collectorHost,'IRIS_UHASH', hash, true);
      },

      trackBusinessEvt(data, context) {
        if (this.snowplowEnabled) {
          if (data) {
            trackSelfDescribingEvent({
              event: {
                schema: 'iglu:com.iiris/business_event/jsonschema/1-0-1',
                data: {
                  timestamp: data.ts,
                  cookieId: this.getDUId() ? this.getDUId() : '',
                  userId: this.getUserId(data.userId),
                  sessionId: data.sessionId,
                  pageURL: window.location.href,
                  entityId: data.entityId,
                  type: data.type,
                  transactionId: data.transactionId,
                  formId: data.formId,
                  status: data.status
                }
              },
              context: this.getAssetContext(context)
            });
          }
        }
      },

      getRecommendParamsFromUrl() {
        try {
          const params = new URLSearchParams(window.location.search);

          return {
            impressionId: params.get("imp"),
            recommendationId: params.get("rec"),
            itemId: params.get("item")
          };
        } catch (e) {
          console.log("Error reading recommend params", e);
          return {};
        }
      },

      isRecommendTraffic() {
        const params = this.getRecommendParamsFromUrl();
        return !!params.impressionId;
      },

      trackRecommendPageLoad() {
        if (!this.snowplowEnabled) return;
        if (window._recommendPageLoaded) return;

       if (!recommendParamsData || (
            !recommendParamsData.impression &&
            (!recommendParamsData.recipe || !recommendParamsData.itemId))) {
             return;
       }
        window._recommendPageLoaded = true;
        window.name = "";
        recommendParamsData.pageUrl = window.location.href;
        window._iirisRecStartTime = Date.now();

        trackSelfDescribingEvent({
          event: {
            schema: "iglu:com.iiris/recommend_page_load/jsonschema/1-0-0",
            data: {
              impressionId: recommendParamsData.impression || "",
              recommendationId: recommendParamsData.recId || null,
              itemId: recommendParamsData.itemId || null
            }
          }
        });
      },

      trackRecommendPageUnload(preserveSession) {
        try {
          if (!this.snowplowEnabled) return;

          if (!window._recommendPageLoaded) return; 
          
          if (!recommendParamsData || (
            !recommendParamsData.impression &&
            (!recommendParamsData.recipe || !recommendParamsData.itemId))) {
             return;
          }

          const endTime = Date.now();
          const startTime = window._iirisRecStartTime || endTime;

          const activeSeconds = Math.floor((endTime - startTime) / 1000);
          const OffSetLimit = (val, min = 0, max = 100000) => {
            if (!isFinite(val)) return min;
            return Math.min(Math.max(Math.round(val), min), max);
          };

          const recPageUrl = recommendParamsData.pageUrl || window.location.href;

          trackSelfDescribingEvent({
            event: {
              schema: "iglu:com.iiris/recommend_page_unload/jsonschema/1-0-1",
              data: {
                impressionId: recommendParamsData.impression || "",
                recommendationId: recommendParamsData.recId || null,
                itemId: recommendParamsData.itemId || null,
                pageUrl: recPageUrl ? recPageUrl.substring(0,4096) : null,
                userId: this.getUserId(),
                activeSeconds: Math.min(Math.max(activeSeconds, 0), 86400),
                minXOffset: OffSetLimit(aggregatedEvent.minXOffset),
                maxXOffset: OffSetLimit(aggregatedEvent.maxXOffset),
                minYOffset: OffSetLimit(aggregatedEvent.minYOffset),
                maxYOffset: OffSetLimit(aggregatedEvent.maxYOffset)
              }
            }
          });
          
          if(!preserveSession){
            window._recommendPageLoaded = false;
            recommendParamsData = null;
            window.name = "";
          }
          
        } catch (err) {
          console.log("Error in recommend_page_unload", err);
        }
      },

      handleHrefCDLink(linkElement) {
        try {
          if (isSameDomainLink(linkElement.href)) {
            return false;
          }
          let cdUrl = new URL(linkElement.href);
          if (cdUrl.protocol !== 'http:' && cdUrl.protocol !== 'https:') {
            return false;
          }
          if (qStrContainsSpParam(cdUrl.href)) {
            return false;
          }
          return getSnowplowDuid() ? true : false;
        }
        catch (e) {
          return false;
        }
      },

      trackPartnerInteractionEvt(data) {
        if (this.snowplowEnabled) {
          const uuid = uuidv4();
          const hash = CryptoJS.MD5(uuid);
          data.interaction_uid = hash.toString(CryptoJS.enc.Hex).toUpperCase();
          data.interaction_date = new Date().toISOString().replace('T', ' ').replace('Z', '')
          trackSelfDescribingEvent({
            event: {
              schema:
                "iglu:com.iiris/pie/jsonschema/1-0-1",
              data: data,
            },
          });
        }
      },

      addClickImpression(sourceUrl, targetUrl, impression, recId, itemId, recipe) {
        let redirectUrl;
        try { this.trackRecommendPageUnload(); } catch (e) {}

        recommendParamsData = {
          impression,
          recId,
          itemId,
          iiris_ref: "recommend",
          placement: "default",
          widgetId: "default",
          recipe,
          url: sourceUrl,
          timeStamp: Date.now()
        };
        // store in window.name
        try {
          window.name = JSON.stringify({
            __iirisRec: recommendParamsData
          });
        } catch (e) {
          console.log("Error storing recommend data", e);
        }
        try {
          redirectUrl = new URL(sourceUrl);
          redirectUrl.searchParams.append("iiris-ref", "recommend");
        } catch (e) {
          redirectUrl =
            sourceUrl +
            `?iiris-ref=recommend`;
        }

        window.IIRISTracker.ad_impression("click", redirectUrl.toString(), targetUrl, impression, recId, itemId, recipe);
      },

    _impressionIds: [],

      getImpression(cards) {
        if (!cards) return null;
        const list = cards.length ? cards : [cards];
        const uniqueIds = new Set();
        for (let i = 0; i < list.length; i++) {
          const card = list[i];
          const link = card.matches("a") ? card : card.querySelector("a");
          const id = this.fetchParams(link?.href).itemId;
          if (id) uniqueIds.add(id);
        }
        this._impressionIds = Array.from(uniqueIds);
        return JSON.stringify(this._impressionIds);
      },

      fetchParams(href) {
        try {
          const u = new URL(href);
          const fragment = new URLSearchParams(u.hash.slice(1));

          return {
            recipe: fragment.get("recipe") || null,
            itemId: fragment.get("source_content_id") || null,
            recId: fragment.get("recommendation_id") || null
          };
        } catch {
          return { recipe: null, itemId: null, recId: null };
        }
      },

      /*** **************************************************************************/
      /*** ************ Private methods - Snowplow tracker ******* ************ *****/
      /*** **************************************************************************/

      initSnowplowConfig(cfg) {
        const appId = cfg.appId;
        const collectorHost = cfg.cDomain;
        const context = cfg.context;
        var cfgCount = (window.IIRISTracker) ? window.IIRISTracker.instCount : 0;
        newTracker("sp1", collectorHost, {
          appId: appId,
          eventMethod: "post",
          keepalive: true,
          discoverRootDomain: true,
          cookieSameSite: "Lax",
          postPath: this.getPostPath(collectorHost),
          crossDomainLinker: this.handleHrefCDLink,
          contexts: {
            performanceNavigationTiming: false,
          },
          plugins: [LinkClickTrackingPlugin(), FormTrackingPlugin(), ButtonClickTrackingPlugin(), MediaTrackingPlugin(), SnowplowEcommercePlugin()],
        });

        setUserIdFromCookie("IRIS_ID");

        enableActivityTracking({
          minimumVisitLength: 30,
          heartbeatDelay: 30
        });
        enableActivityTrackingCallback({
          minimumVisitLength: 30,
          heartbeatDelay: 30,
          callback: function (event) {
            aggregatedEvent = {
              minXOffset: aggregatedEvent.minXOffset < event.minXOffset ? aggregatedEvent.minXOffset : event.minXOffset,
              maxXOffset: aggregatedEvent.maxXOffset > event.maxXOffset ? aggregatedEvent.maxXOffset : event.maxXOffset,
              minYOffset: aggregatedEvent.minYOffset < event.minYOffset ? aggregatedEvent.minYOffset : event.minYOffset,
              maxYOffset: aggregatedEvent.maxYOffset > event.maxYOffset ? aggregatedEvent.maxYOffset : event.maxYOffset,
              numEvents: aggregatedEvent.numEvents + 1
            };
          }
        });
        window.addEventListener("scroll", function () {
          const yOffset = window.scrollY || window.pageYOffset;
          aggregatedEvent.maxYOffset = Math.max(aggregatedEvent.maxYOffset, yOffset);
          aggregatedEvent.minYOffset = Math.min(aggregatedEvent.minYOffset, yOffset);
        });

      //Capture First Video Element
      const initMediaTracking = (videoEl) => {
        if (videoEl.dataset.spMediaTracked) return;
        videoEl.dataset.spMediaTracked = "true";

        startHtml5MediaTracking({
          id: uuidv4(),
          video: videoEl,
          captureEvents: [
            'play',
            'pause',
            'ended',
            'seek',
            'volume_change',
            'ping'
          ],
          boundaries: [25, 50, 75]
        });
        console.log('Media tracking initialized');
      };

      window.testMediaTracking = initMediaTracking;

      const videos = document.querySelectorAll('video');

      videos.forEach((videoEl) => {
        if (videoEl.readyState >= 1) {
          initMediaTracking(videoEl);
        } else {
          videoEl.addEventListener(
            'loadedmetadata',
            () => initMediaTracking(videoEl),
            { once: true }
          );
        }
      });

      const mediaObserver = new MutationObserver(() => {
        const videos = document.querySelectorAll('video');

        videos.forEach((videoEl) => {
          if (!videoEl.dataset.spMediaTracked) {
            console.log('Late-loaded video detected');

            if (videoEl.readyState >= 1) {
              initMediaTracking(videoEl);
            } else {
              videoEl.addEventListener(
                'loadedmetadata',
                () => initMediaTracking(videoEl),
                { once: true }
              );
            }
          }
        });
      });

      mediaObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      //////////////////// Vimeo Tracking ///////////
      function initVimeoTracking() {
        document
          .querySelectorAll('iframe[src*="player.vimeo.com"]')
          .forEach(trackVimeoIframe);
      }

      function trackVimeoIframe(iframe) {
        if (
          !iframe ||
          iframe.dataset.spVimeoTracked === 'true'
        ) {
          return;
        }

        try {
          const mediaId = `vimeo-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 9)}`;

          startVimeoTracking({
            id: mediaId,
            video: iframe
          });

          iframe.dataset.spVimeoTracked = 'true';
          iframe.dataset.spVimeoId = mediaId;
        } catch (err) {
          console.error('[Snowplow] Vimeo tracking failed', err);
        }
      }

      function untrackVimeoIframe(iframe) {
        const mediaId = iframe?.dataset?.spVimeoId;

        if (!mediaId) {
          return;
        }

        try {
          endVimeoTracking(mediaId);

          delete iframe.dataset.spVimeoTracked;
          delete iframe.dataset.spVimeoId;
        } catch (err) {
          console.error('[Snowplow] Vimeo cleanup failed', err);
        }
      }

      function observeVimeoPlayers() {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {

            mutation.addedNodes.forEach((node) => {
              if (!(node instanceof HTMLElement)) {
                return;
              }

              if (
                node.tagName === 'IFRAME' &&
                node.src?.includes('player.vimeo.com')
              ) {
                trackVimeoIframe(node);
              }

              node
                .querySelectorAll?.(
                  'iframe[src*="player.vimeo.com"]'
                )
                .forEach(trackVimeoIframe);
            });

            mutation.removedNodes.forEach((node) => {
              if (!(node instanceof HTMLElement)) {
                return;
              }

              if (
                node.tagName === 'IFRAME' &&
                node.dataset?.spVimeoId
              ) {
                untrackVimeoIframe(node);
              }

              node
                .querySelectorAll?.(
                  'iframe[data-sp-vimeo-id]'
                )
                .forEach(untrackVimeoIframe);
            });
          });
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        return observer;
      }

      if (!window.spVimeoObserver) {
        initVimeoTracking();
        window.spVimeoObserver = observeVimeoPlayers();
      }

      ////////////// End fo Vimeo Tracking ///////////
     
      let trackedForms = new WeakSet();
      let nativeFormTrackingEnabled = false;

      function activateNativeFormTracking() {
        if (nativeFormTrackingEnabled) return;
        nativeFormTrackingEnabled = true;

        console.log("Enabling native form tracking");
        enableFormTracking({
          options: {
            events: ['change_form','submit_form'],
            forms: { filter: isNativeTrackable },
            fields: {
              // do not send sensitive fields
              denylist: [],
              filter: isFieldAllowed,

              // transformation to hashed values
              transform: FormFieldEncryption
            }
          }
        });
      }

      // MutationObserver only fires for *future* DOM changes, so a <form>
      // already present at load time (the common case for a plain HTML
      // page) has to be checked explicitly here - otherwise neither native
      // tracking would turn on nor would virtual tracking correctly stay
      // off until some unrelated later mutation happened to run the
      // observer callback.
      const initialForms = document.querySelectorAll("form");

      let disableVirtualFormTracking = initialForms.length === 0
        ? enableVirtualFormTracking({
            filter: isFieldAllowed,
            transform: FormFieldEncryption
          })
        : null;

      if (initialForms.length > 0) {
        initialForms.forEach(f => trackedForms.add(f));
        activateNativeFormTracking();
      }
      const observer = new MutationObserver(() => {
        const form = document.querySelectorAll("form");
        if (form.length === 0) return;

         if (disableVirtualFormTracking) {
          disableVirtualFormTracking();
          disableVirtualFormTracking = null;
        }

        let newFormFound = false;
        form.forEach(f => {
          if (!trackedForms.has(f)) {
            trackedForms.add(f);
            newFormFound = true;
          }
        });
        form.forEach(form => {
          if (!form.dataset.tracked) {
            form.dataset.tracked = "true";
          }
        });
        if (newFormFound) {
          console.log("New form detected → enabling tracking");

          activateNativeFormTracking();
        }
      });

      // watch for dynamically loaded forms (React / widgets)
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      // Form submit code - End


        if (window.IIRISTracker && window.IIRISTracker.instCount > 0) {
          crossDomainLinker(this.handleHrefCDLink);
        }

        /**
         * Event listeners
         */
        var me = this;

        if (!navHooksInstalled) {
          navHooksInstalled = true;
          const endRecommendView = function () {
            if (recommendParamsData) {
              try { me.trackRecommendPageUnload(); } catch (e) {}
            }
          };
          let lastRecUrl = location.href;
          const onRecLocationChange = function () {
            if (location.href === lastRecUrl) return; 
            lastRecUrl = location.href;
            endRecommendView();
          };
          ['pushState', 'replaceState'].forEach(function (m) {
            const orig = history[m];
            if (typeof orig !== 'function' || orig.__irisWrapped) return;
            const wrapped = function () {
              const r = orig.apply(this, arguments); 
              try { onRecLocationChange(); } catch (e) {}
              return r;
            };
            wrapped.__irisWrapped = true;
            history[m] = wrapped;
          });
          window.addEventListener('hashchange', onRecLocationChange);
        }

        document.addEventListener("visibilitychange", function () {
          if (window.IIRISTracker && cfgCount == window.IIRISTracker.instCount) {
            if (document.visibilityState == "hidden") {
              me.trackUnloadEvent(cfg);
              me.trackRecommendPageUnload(true);
            }
          }
        });

        window.addEventListener('recommendCTREvent', function (evt) {
          if (window.IIRISTracker && cfgCount == window.IIRISTracker.instCount) {
            let data = {}
            data.url = evt.detail.target_url
            me.trackUriRedirect(data)
          }
        });

        window.addEventListener("popstate", function () {
          if (window.IIRISTracker && cfgCount == window.IIRISTracker.instCount) {
            me.trackUnloadEvent(cfg);
            me.trackRecommendPageUnload();
          }
          resetTrackingState();
        });

        window.addEventListener("crossDomainClick", function (evt) {
          if (window.IIRISTracker && cfgCount == window.IIRISTracker.instCount) {
            me.handleCrossDomainLink(evt);
          }
        });

        // Register DOM enhancement
        enableDomClickTracking();

        enableButtonClickTracking({ filter: (el) => !isEntryCta(el) });

        window.addEventListener('click', function (evt) {
          if (!window.IIRISTracker || cfgCount != window.IIRISTracker.instCount) return;
          const clicked = evt.composed ? evt.composedPath()[0] : evt.target;
          const el = clicked && clicked.closest ? clicked.closest('button') : null;
          if (!el || !isEntryCta(el)) return;

          trackButtonClick({
            label: label(el),
            id: el.id || undefined,
            classes: el.classList.length ? Array.from(el.classList) : undefined,
            name: sourceValue(el),
          });
        }, true);

        enableEnterTracking();

        enableEnhancedEcommerceTracking();

        enableGenericFormTracking({
          filter: isFieldAllowed,
          transform: FormFieldEncryption
        });

        let cdlRescanQueued = false;
        new MutationObserver(function () {
          if (cdlRescanQueued) return;
          cdlRescanQueued = true;
          requestAnimationFrame(function () {
            cdlRescanQueued = false;
            crossDomainLinker(me.handleHrefCDLink);
          });
        }).observe(document.body, { childList: true, subtree: true });

        try {
          if (window.name) {
            const parsed = JSON.parse(window.name);

            if (parsed && parsed.__iirisRec) {
              recommendParamsData = parsed.__iirisRec;
            }
          }
            if (!recommendParamsData) {
              const { recipe, itemId, recId } = this.fetchParams(window.location.href);

              if (recipe && itemId) {
                recommendParamsData = {
                  impression: "",
                  recId,
                  recipe,
                  itemId,
                };
              }
            }
        } catch (e) {
          console.log("Error restoring recommend data", e);
        }

        // Snowplow Pageview with Custom Context
        spTrackPageView({
          context: this.getContext(context)
        });
          this.trackRecommendPageLoad();

        this.removeSpParam();

        this.collectorHost = collectorHost;
        this.appId = appId;

        // Separated duid as a cookie
        var spduid = getSnowplowDuid();
        if (spduid) {
          /* document.cookie = "_iris_duid=" + spduid + "; path=/;";*/
          setDomainCookie(this.collectorHost, '_iris_duid', spduid);
        }

        // =====================================================
        // IRIS Recommendation Served Impression Tracking
        // =====================================================

        const servedContentIds = new Set();
        const servedObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (!(node instanceof HTMLElement)) return;

              const cards = node.matches?.("a.Iris-Rec-Card")
                ? [node]
                : node.querySelectorAll?.("a.Iris-Rec-Card");

              cards?.forEach((el) => {
                if (processedCards.has(el)) return;
                processedCards.add(el);

                const data = extractRecommendationData(el);
                if (!data.href) return;

                const contentId = window.IIRISTracker.fetchParams(data.href).itemId;
                if (!contentId || servedContentIds.has(contentId)) return;
                servedContentIds.add(contentId);

                el.dataset.iirisImpressionId = data.impressionId;
                el.dataset.iirisRecommendationId = data.recommendationId;
                el.dataset.iirisItemId = data.itemId;
                el.dataset.iirisRecipe = data.recipe;
                el.dataset.iirisProcessed = "true";

                window.IIRISTracker.trackServedImpression({
                  impression_id: data.impressionId,
                  recommendation_id: data.recommendationId
                });
              });
            });
          });
        });

        function extractRecommendationData(el) {
          if (!el) return {};
          const linkEl = el.matches("a") ? el : el.querySelector("a");
          const href = linkEl?.href || null;
          // const impression = window.IIRISTracker.getImpression([el]);
          const allCards = document.querySelectorAll("a.Iris-Rec-Card");
          const impression = window.IIRISTracker.getImpression(allCards);

          return {
            element: el,
            href: href,
            itemId: el.dataset?.itemId || href, // fallback to URL
            recommendationId: el.dataset?.recommendationId || null,
            impressionId: impression,
            recipe: el.dataset?.recipe || "default"
          };
        }

        function processExistingRecommendationCards() {
          const cards = document.querySelectorAll("a.Iris-Rec-Card");

          cards.forEach((el) => {
            if (processedCards.has(el)) return;
            processedCards.add(el);

            const data = extractRecommendationData(el);

            if (!data.href) return;

            const contentId = window.IIRISTracker.fetchParams(data.href).itemId;
            if (!contentId || servedContentIds.has(contentId)) return;
            servedContentIds.add(contentId);

            // attach dataset
            el.dataset.iirisImpressionId = data.impressionId;
            el.dataset.iirisRecommendationId = data.recommendationId;
            el.dataset.iirisItemId = data.itemId;
            el.dataset.iirisRecipe = data.recipe;
            el.dataset.iirisProcessed = "true";

            window.IIRISTracker.trackServedImpression({
              impression_id: data.impressionId,
              recommendation_id: data.recommendationId
            });
          });
        }

        servedObserver.observe(document.body, {
          childList: true,
          subtree: true
        });

        processExistingRecommendationCards();

        // =====================================================
        // IRIS Recommendation Impressions Tracking
        // =====================================================
        window.addEventListener("click", function (e) {

          const card = e.target.closest("a.Iris-Rec-Card");
          if (!card) return;
          const link = card.matches("a") ? card : card.querySelector("a");
          if (!link || !link.href) return;


          const recmndUrl = link.href;
          const targetUrl = link.href;
          const { recipe, itemId, recId } = window.IIRISTracker.fetchParams(link.href);
          const target = card.getAttribute("target") === "_blank" ? "_blank" : "_self";
          let redirectUrl = recmndUrl; // fallback
          const impression = window.IIRISTracker.getImpression(document.querySelectorAll("a.Iris-Rec-Card"));

          if (window.IIRISTracker.addClickImpression) {
            window.IIRISTracker.addClickImpression(recmndUrl, targetUrl, impression, recId, itemId, recipe);
          }

         setTimeout(() => {
            if (target === "_blank") {
              window.open(redirectUrl, "_blank");
            } else {
              window.location.href = redirectUrl;
            }
          }, 500);
      });

        window.addEventListener("mouseover", function (e) {

          const card = e.target.closest("a.Iris-Rec-Card");

          if (!card) return;
          if (card.contains(e.relatedTarget)) return;
          const link = card.matches("a") ? card : card.querySelector("a");
          if (!link || !link.href) return;


          const recUrl = link.href;
          const targetUrl = link.href;
          const { recipe, itemId, recId } = window.IIRISTracker.fetchParams(link.href);
          const impression = window.IIRISTracker.getImpression(document.querySelectorAll("a.Iris-Rec-Card"));

          if (window.IIRISTracker && window.IIRISTracker.ad_impression) {
            window.IIRISTracker.ad_impression("onMouseEnter", recUrl, targetUrl, impression, recId, itemId, recipe);
          }
        });

        const trackedViewportCards = new Set();
        const viewportContentIds = new Set();

        window.addEventListener("scroll", function () {

          const cards = document.querySelectorAll("a.Iris-Rec-Card");
          for (const card of cards) {
            if (trackedViewportCards.has(card)) continue;
            const rect = card.getBoundingClientRect();
            if (
              rect.top >= 0 &&
              rect.left >= 0 &&
              rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
              rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            ) {
              trackedViewportCards.add(card);
              const { recipe, itemId, recId } = window.IIRISTracker.fetchParams(card.href);
              if (!itemId || viewportContentIds.has(itemId)) continue;
              viewportContentIds.add(itemId);
              const recUrl = card.href;
              const targetUrl = card.href;
              const impression = window.IIRISTracker.getImpression(cards);
              window.IIRISTracker.ad_impression("viewport", recUrl, targetUrl, impression, recId, itemId, recipe);
            }
          }
        });

      },

      getPageIsSponsored(context) {
        var pageIsSpons = context['pageIsSponsored'];
        var boolPgRes = false;
        if (typeof (pageIsSpons) == 'string') {
          boolPgRes = pageIsSpons.toLowerCase() == 'true';
        }
        else {
          boolPgRes = pageIsSpons
        }
        return boolPgRes;
      },

      getPageDesc(desc) {
        if (desc) {
          return desc;
        }
        else {
          return (document.querySelector('meta[name="description"]') ? document.querySelector('meta[name="description"]').content : '');
        }

      },

      getSecondaryTerms(secTerms) {
        if (secTerms) {
          if (Array.isArray(secTerms))
            return secTerms.join(',');
          else
            return secTerms;
        }
        return secTerms;
      },

        validateContextInput(input, maxLength) {

        // Explicit null handling
        if (input === null || input === undefined) {
          return null;
        }

        // String case
        if (typeof input === 'string') {
          return input.length > maxLength
            ? input.substring(0, maxLength)
            : input;
        }

        // Array case (guarded)
        if (Array.isArray(input)) {
          const validStrings = input.filter(item => typeof item === 'string');

          if (validStrings.length === 0) {
            return null;
          }

          const joinedValue = validStrings.join('|');

          return joinedValue.length > maxLength
            ? joinedValue.substring(0, maxLength)
            : joinedValue;
        }

        // All other types (object, boolean, number)
        return null;
      },

      getContext(context) {
        context = resolvePageContext(context);
        return [{
          schema: "iglu:com.iiris/content_taxonomy_ctx/jsonschema/1-0-15",
          data: {
            "byline": this.validateContextInput(context['byline'], 1024),
            "canonicalUrl": this.validateContextInput(context['canonicalUrl'],4096),
            "contentAuthor": this.validateContextInput(context['contentAuthor'],1024),
            "contentLabel": this.validateContextInput(context['contentLabel'],1024),
            "contentSource": this.validateContextInput(context['contentSource'],4096),
            "gatingLevel": this.validateContextInput(context['gatingLevel'],4096),
            "contentPubDate": this.validateContextInput(context['contentPubDate'],1024),
            "pageID": this.validateContextInput(context['pageID'],2048),
            "pageDesc": this.validateContextInput(this.getPageDesc(context['pageDesc']),8192),
            "pageIsSponsored": this.getPageIsSponsored(context),
            "pageSponsor": this.validateContextInput(context['pageSponsor'],1024),
            "pageTitle": this.validateContextInput(context['pageTitle'],1024),
            "pageType": this.validateContextInput(context['pageType'],256),
            "primaryTerm": this.validateContextInput(context['primaryTerm'],1024),
            "auxiliaryTerm": this.validateContextInput(this.getSecondaryTerms(context['secondaryTerm']),2048),
            "tags":this.validateContextInput(context['tags'],1024)
          }
        }];
      },

      getAssetContext(context) {
        return [{
          schema: "iglu:com.iiris/asset/jsonschema/2-0-1",
          data: {
            "id": context['id'],
            "name": context['name'],
            "type": context['type'],
            "created": context['created'],

            "lastUpdated": context['lastUpdated'],
            "url": context['url'],
            "tags": context['tags'],
            "metadata": context['metadata'],
          }
        }];
      },

      getPostPath(collectorHost) {
        if (collectorHost === 'com-informa-prod1.mini.snplow.net') {
          return '/com.snowplowanalytics.snowplow/tp2';
        }
        else
          return '/com.iiris/ed0';
      },

      /*** **************************************************************************/
      /*** *********************   Private methods - Lytics tracker ******* *********/
      /*** **************************************************************************/
      initLyticsConfig() {
        !function () { "use strict"; var o = window.jstag || (window.jstag = {}), r = []; function n(e) { o[e] = function () { for (var n = arguments.length, t = new Array(n), i = 0; i < n; i++)t[i] = arguments[i]; r.push([e, t]) } } n("send"), n("mock"), n("identify"), n("pageView"), n("unblock"), n("getid"), n("setid"), n("loadEntity"), n("getEntity"), n("on"), n("once"), n("call"), o.loadScript = function (n, t, i) { var e = document.createElement("script"); e.async = !0, e.src = n, e.onload = t, e.onerror = i; var o = document.getElementsByTagName("script")[0], r = o && o.parentNode || document.head || document.body, c = o || r.lastChild; return null != c ? r.insertBefore(e, c) : r.appendChild(e), this }, o.init = function n(t) { return this.config = t, this.loadScript(t.src, function () { if (o.init === n) throw new Error("Load error!"); o.init(o.config), function () { for (var n = 0; n < r.length; n++) { var t = r[n][0], i = r[n][1]; o[t].apply(o, i) } r = void 0 }() }), this } }();
        // Define config and initialize Lytics tracking tag.
        jstag.init({
          src: '//c.lytics.io/api/tag/45b7a2fa48338cf11ef06bb0386e4843/latest.min.js',
          loadid: true
        });
      },

      /*** **************************************************************************/
      /*** *********************   Private methods - Eloqua Hash Email ******** *****/
      /*** **************************************************************************/
      setEloquaHashEmail() {
        const params = new Proxy(new URLSearchParams(window.location.search), {
          get: (searchParams, prop) => searchParams.get(prop)
        });
        let value = params.sp_eh;
        if (value) {
          /*document.cookie = "_iris_eloqua_em=" + value + "; path=/;";*/
          setDomainCookie(this.collectorHost, '_iris_eloqua_em', value);

        }
      },
    };

    return trackerObj;
  }

  var aggregatedEvent = {
    minXOffset: Infinity,
    maxXOffset: 0,
    minYOffset: Infinity,
    maxYOffset: 0,
    numEvents: 0
  };
  let pageStartTime = Date.now();

  function qStrContainsSpParam(cdUrl) {
    if (cdUrl.includes('?_sp=') || cdUrl.includes('&_sp=')) {
      return true;
    }
    else
      return false;
  }

  function isSameDomainLink(urlStr) {
    try {
      return new URL(urlStr, window.location.href).origin === window.location.origin;
    } catch (e) {
      return true;
    }
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) return match[2];
  }

  function resolvePageContext(context) {
    var ctx = context || {};
    return Object.assign({}, ctx, {
      canonicalUrl: ctx.canonicalUrl || window.location.href,
      pageTitle: ctx.pageTitle || document.title
    });
  }

  function setDomainCookie(collectorHost, name, val, age) {
    try {
      let domainName = collectorHost ? collectorHost.substring(1, collectorHost.length) : collectorHost;
      let ageString = age === true? 'max-age=7776000; samesite=strict; secure;': '';
      if (collectorHost && collectorHost.startsWith('c.') && (window.location.hostname.indexOf(domainName) > 0)) {
        document.cookie = name + "=" + val + "; path=/;"+ ageString +" domain=" + domainName + ";";
      }
      else {
        document.cookie = name + "=" + val + "; path=/;" + ageString;
      }
    } catch (err) {
      console.log('Domain cookie is not set');
    }
  }

  /* returns the SP domain user id */
  function getSnowplowDuid(cookieName) {
    cookieName = cookieName || '_sp_';
    var matcher = new RegExp(cookieName + 'id\\.[a-f0-9]+=([^;]+);?');
    var match = document.cookie.match(matcher);
    if (match && match[1]) {
      return match[1].split('.')[0];
    } else {
      return false;
    }
  }


  //backwards compatibility
  if (typeof window.snowplow === "undefined") {
    window.snowplow = function (command, ...args) {
      switch (command) {
        case "newTracker":
          return newTracker(...args);
        case "trackPageView":
          return spTrackPageView(...args);
        case "trackSelfDescribingEvent":
          return trackSelfDescribingEvent(...args);
        case "trackStructEvent":
          return trackStructEvent(...args);
        case "setUserIdFromCookie":
          return setUserIdFromCookie(...args);
        case "clearUserData":
          return clearUserData(...args);
        case "enableActivityTracking":
          return enableActivityTracking(...args);
        case "enableActivityTrackingCallback":
          return enableActivityTrackingCallback(...args);
        case "enableLinkClickTracking":
          return enableLinkClickTracking(...args);
        case "enableFormTracking":
          return enableFormTracking(...args);
        case "crossDomainLinker":
          return crossDomainLinker(...args);
        default:
          console.warn("window.snowplow: unsupported command: " + command);
      }
    };
  }

  if (typeof window.IIRISTracker === "undefined") {
    window.IIRISTracker = initTracker();
    window.IIRISTracker.instCount = 0;
    var event = new Event("trackerInitialized");
    window.dispatchEvent(event);
  }
  else {
    if (window.IIRISTracker && !(typeof window.IIRISTracker.instCount === "undefined")) {
      window.IIRISTracker.instCount = window.IIRISTracker.instCount + 1;
    }
  }
})(window);
